import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError } from "./lib/format.js";
import { getActiveDivisions, loadClinicModules } from "./data.js";

// DIVISIONS dibangun dinamis dari modul aktif (+ opsi ALL)

const emptyForm = {
  name:                    "",
  nik:                     "",
  phone:                   "",
  email:                   "",
  status:                  "ACTIVE",
  division:                "WELLNESS",
  competencies:            "",
  nomor_str:               "",
  nomor_sip:               "",
};

function satusehatStatusLabel(row) {
  const labels = {
    FOUND: "IHS ditemukan",
    MULTIPLE_FOUND: "Perlu pilih IHS",
    NOT_FOUND: "Tidak ditemukan",
    NOT_FOUND_SANDBOX: "Sandbox: tidak ada",
    ERROR: "Error",
  };
  return labels[row?.satusehat_practitioner_status] || (row?.nik ? "Belum dicek" : "NIK kosong");
}

function SatusehatNakesBadge({ row }) {
  const status = row?.satusehat_practitioner_status || "";
  const styleMap = {
    FOUND: { background: "#dcfce7", color: "#15803d", border: "#bbf7d0" },
    MULTIPLE_FOUND: { background: "#fef3c7", color: "#b45309", border: "#fde68a" },
    NOT_FOUND: { background: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
    NOT_FOUND_SANDBOX: { background: "#e0f2fe", color: "#0369a1", border: "#bae6fd" },
    ERROR: { background: "#fee2e2", color: "#b91c1c", border: "#fecaca" },
  };
  const style = styleMap[status] || { background: "#f3f4f6", color: "#6b7280", border: "#e5e7eb" };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{
        alignSelf: "flex-start",
        background: style.background,
        border: `1px solid ${style.border}`,
        borderRadius: 6,
        color: style.color,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: 1.2,
        padding: "3px 8px",
        whiteSpace: "nowrap",
      }}>
        {satusehatStatusLabel(row)}
      </span>
      <small style={{ color: "#6b7280" }}>{row?.satusehat_practitioner_id || "Belum ada IHS"}</small>
    </div>
  );
}

function generateNakesId() {
  return "NAK-" + crypto.randomUUID().split("-")[0].toUpperCase();
}

export function MasterNakesPage() {
  const [activeModules, setActiveModules] = useState({ wellness: true, estetik: true });
  const DIVISIONS = [
    ...getActiveDivisions(activeModules),
    { value: "ALL", label: "Semua Divisi" },
  ];
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [form, setForm]           = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [search, setSearch]       = useState("");

  async function fetchStaff() {
    setLoading(true);
    setError("");
    try {
      const { data, error: e } = await supabase
        .from("staff")
        .select("id,name,nik,phone,email,status,division,competencies,nomor_str,nomor_sip,satusehat_practitioner_id,satusehat_practitioner_status,satusehat_practitioner_checked_at,satusehat_practitioner_error")
        .eq("clinic_id", CLINIC_ID)
        .order("name");
      if (e) throw e;
      setRows(data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStaff();
    loadClinicModules().then(setActiveModules).catch(() => {});
  }, []);

  function updateField(key, val) { setForm((p) => ({ ...p, [key]: val })); }
  function resetForm() { setForm(emptyForm); setEditingId(null); setShowForm(false); setError(""); }

  function openCreateForm() {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function startEdit(row) {
    setEditingId(row.id);
    setShowForm(true);
    setForm({
      name:                     row.name                     || "",
      nik:                      row.nik                      || "",
      phone:                    row.phone                    || "",
      email:                    row.email                    || "",
      status: /INACTIVE|Nonaktif/i.test(row.status || "") ? "INACTIVE" : "ACTIVE",
      division:                 row.division                 || "WELLNESS",
      competencies:             row.competencies             || "",
      nomor_str:                row.nomor_str                || "",
      nomor_sip:                row.nomor_sip                || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Nama nakes wajib diisi."); return; }
    setSaving(true);
    setError("");
    try {
      const payload = {
        clinic_id:                CLINIC_ID,
        name:                     form.name.trim(),
        nik:                      form.nik.trim() || null,
        phone:                    form.phone.trim()        || null,
        email:                    form.email.trim()        || null,
        status:                   form.status              || "ACTIVE",
        division:                 form.division            || "WELLNESS",
        competencies:             form.competencies.trim() || null,
        nomor_str:                form.nomor_str.trim()    || null,
        nomor_sip:                form.nomor_sip.trim()    || null,
      };
      if (editingId) {
        const { error: ue } = await supabase.from("staff").update(payload).eq("id", editingId);
        if (ue) throw ue;
      } else {
        const { error: ie } = await supabase.from("staff").insert({
          ...payload,
          id: generateNakesId(),
          base_salary: 0,
          allowance_per_attendance: 0,
          default_commission: 0,
          payroll_type: "Standar",
          profit_share_rate: 0,
        });
        if (ie) throw ie;
      }
      resetForm();
      await fetchStaff();
      setShowForm(false);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Nonaktifkan nakes "${row.name}"?\n\nData kehadiran & riwayat tetap tersimpan.`)) return;
    setError("");
    try {
      const { error: de } = await supabase
        .from("staff")
        .update({ status: "INACTIVE" })
        .eq("id", row.id);
      if (de) throw de;
      if (editingId === row.id) resetForm();
      await fetchStaff();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  const filtered = search.trim()
    ? rows.filter((r) =>
        (r.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (r.phone || "").includes(search) ||
        (r.competencies || "").toLowerCase().includes(search.toLowerCase())
      )
    : rows;

  return (
    <div className="page-stack">

      {/* ── Page Heading ── */}
      <div className="page-heading">
        <div>
          <p>Kelola data dokter, terapis, dan staf klinis klinik.</p>
        </div>
        <button type="button" className="button button-primary nakes-add-button" onClick={openCreateForm}>
          <Plus size={17} /> Tenaga Kesehatan
        </button>
      </div>

      {/* ── Form ── */}
      {showForm && (
      <article className="panel" style={{ padding: '1.5rem' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Master Data XV</span>
            <h3>{editingId ? "Edit Nakes" : "Tambah Tenaga Kesehatan"}</h3>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            <strong>Terjadi kesalahan.</strong> {error}
          </div>
        )}

        <form className="nakes-form" onSubmit={handleSubmit}>

          {/* Baris 1: Nama, Telepon, Status */}
          <div className="nf-row">
            <div className="nf-field" style={{ flex: 3 }}>
              <label>Nama Lengkap *</label>
              <input value={form.name} onChange={(e) => updateField("name", e.target.value)}
                placeholder="dr. Nama / Nama Terapis" required />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>NIK Nakes</label>
              <input value={form.nik} onChange={(e) => updateField("nik", e.target.value)}
                maxLength={16} inputMode="numeric" placeholder="16 digit untuk SATUSEHAT" />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Telepon</label>
              <input value={form.phone} onChange={(e) => updateField("phone", e.target.value)}
                placeholder="08xxxxxxxxxx" />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Email Notifikasi</label>
              <input type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)}
                placeholder="dokter@email.com" />
            </div>
            <div className="nf-field">
              <label>Status</label>
              <select value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </select>
            </div>
          </div>

          {/* Baris 2: Divisi, Kompetensi */}
          <div className="nf-row">
            <div className="nf-field">
              <label>Divisi</label>
              <select value={form.division} onChange={(e) => updateField("division", e.target.value)}>
                {DIVISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div className="nf-field" style={{ flex: 3 }}>
              <label>Kompetensi / Spesialisasi</label>
              <input value={form.competencies} onChange={(e) => updateField("competencies", e.target.value)}
                placeholder="Akupunktur, Tuina, Bekam… / Facial, Botox, Filler…" />
            </div>
          </div>

          {/* Baris 3: STR, SIP */}
          <div className="nf-row">
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Nomor STR</label>
              <input value={form.nomor_str} onChange={(e) => updateField("nomor_str", e.target.value)}
                placeholder="Surat Tanda Registrasi" />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Nomor SIP</label>
              <input value={form.nomor_sip} onChange={(e) => updateField("nomor_sip", e.target.value)}
                placeholder="Surat Izin Praktik" />
            </div>
          </div>

          <div className="nf-actions">
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Menyimpan…" : editingId ? "Perbarui" : "Simpan"}
            </button>
            {editingId && (
              <button type="button" className="button button-quiet" onClick={resetForm}>Batal Edit</button>
            )}
          </div>
        </form>
      </article>
      )}

      {/* ── Tabel ── */}
      <article className="panel table-panel" style={{ padding: '1.5rem 1.5rem 1.25rem' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Tenaga Kesehatan Klinik</span>
            <h3>Daftar Nakes &amp; Terapis</h3>
          </div>
          <input className="search-input" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama, telepon, kompetensi…" />
        </div>

        {loading ? (
          <div className="content-loading">
            <div className="loader" />
            <span>Memuat data nakes…</span>
          </div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>NAMA</th>
                <th>DIVISI</th>
                <th>KOMPETENSI</th>
                <th>STR / SIP</th>
                <th>SATUSEHAT</th>
                <th>STATUS</th>
                <th>AKSI</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  <td>
                    <strong>{row.name}</strong>
                    {row.phone && <div style={{ fontSize: "12px", color: "#888", marginTop: 2 }}>{row.phone}</div>}
                    {row.email && <div style={{ fontSize: "12px", color: "#888", marginTop: 2 }}>{row.email}</div>}
                  </td>
                  <td>
                    <span className={`status ${row.division === "ESTETIK" ? "status-info" : row.division === "ALL" ? "status-neutral" : "status-positive"}`}>
                      {row.division === "ESTETIK" ? "Estetik" : row.division === "ALL" ? "Semua" : "Wellness"}
                    </span>
                  </td>
                  <td style={{ color: "#6b6555", fontSize: "13px" }}>{row.competencies || "—"}</td>
                  <td style={{ fontSize: "12px", lineHeight: "1.6" }}>
                    {row.nomor_str ? <div><b>STR</b> {row.nomor_str}</div> : null}
                    {row.nomor_sip ? <div><b>SIP</b> {row.nomor_sip}</div> : null}
                    {!row.nomor_str && !row.nomor_sip ? "—" : null}
                  </td>
                  <td><SatusehatNakesBadge row={row} /></td>
                  <td>
                    <span className={`status ${/INACTIVE|Nonaktif/i.test(row.status || "") ? "status-danger" : "status-positive"}`}>
                      {/INACTIVE|Nonaktif/i.test(row.status || "") ? "Nonaktif" : "Aktif"}
                    </span>
                  </td>
                  <td>
                    <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                    {" "}
                    <button className="link-button link-danger" onClick={() => handleDelete(row)}>Nonaktifkan</button>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr>
                  <td colSpan={7} className="muted" style={{ padding: "16px 12px" }}>
                    Belum ada data nakes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .nakes-form { display: flex; flex-direction: column; gap: 0; }
        .nakes-add-button { display: inline-flex; align-items: center; gap: 8px; }
        .nf-row {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          margin-bottom: 14px;
        }
        .nf-field {
          flex: 1;
          min-width: 160px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .nf-field label {
          font-size: 12px;
          font-weight: 600;
          color: #6b6555;
          white-space: nowrap;
        }
        .nf-field input,
        .nf-field select {
          padding: 9px 12px;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          font-size: 14px;
          background: #fff;
          width: 100%;
          box-sizing: border-box;
        }
        .nf-actions {
          display: flex;
          gap: 10px;
          margin-top: 6px;
        }
        .search-input {
          padding: 8px 12px;
          border: 1px solid #e8e2d5;
          border-radius: 8px;
          font-size: 13px;
          min-width: 240px;
        }
        .mt-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .mt-table th {
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #7a7364;
          padding: 10px 12px;
          border-bottom: 1px solid #e8e2d5;
        }
        .mt-table td { padding: 12px; border-bottom: 1px solid #f1ece0; }
        .link-button { background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #2f6f4f; cursor: pointer; }
        .link-danger { color: #c0392b; }
      `}</style>
    </div>
  );
}
